import type {
  PublicIncidentRequest,
  PublicLookupData,
  TagType,
} from "@/lib/api/client"
import type { PublicUploadReference } from "@/features/public/report/upload-reference"
import type {
  SafetyQrReportingFieldDefinition,
  SafetyQrReportingTemplateActionDefinition,
  SafetyQrReportingTemplateDefinition,
} from "@/lib/safetyqr-reporting-templates"

export type ReportKind = "behavior" | "incident"
export type IncidentType =
  | "accident"
  | "breakdown"
  | "fire"
  | "hazard"
  | "harassment"
  | "medical"
  | "theft"
export type BehaviorType =
  | "unsafe_driving"
  | "wrong_way_or_lane_violation"
  | "unsafe_parking_or_blocking"
  | "phone_use_or_distraction"
  | "road_rage_or_abuse"
  | "vehicle_condition_issue"
  | "tampering_or_theft_attempt"
  | "other_behavior"
export type Severity = "critical" | "minor" | "moderate" | "severe"

export interface ReportGpsCoordinates {
  accuracy: number
  latitude: number
  longitude: number
}

export interface ReportFormState {
  behaviorType: BehaviorType
  delay: string
  description: string
  deviceTag: string
  incidentType: IncidentType
  injuryStatus: string
  locationDescription: string
  peopleInvolved: string
  reportKind: ReportKind
  reporterEmail: string
  reporterName: string
  reporterPhone: string
  severity: Severity | ""
  vehicleReference: string
}

export interface BuildIncidentPayloadInput {
  form: ReportFormState
  gpsCoordinates: ReportGpsCoordinates | null
  lookup: PublicLookupData | null
  preferredLanguage: "en" | "hi" | "ar" | null
  uploadedPhotos: PublicUploadReference[]
  uploadedVoiceNotes: PublicUploadReference[]
}

export type TemplateReportFieldValue = boolean | string

export interface TemplateReportFormState {
  deviceTag: string
  values: Record<string, TemplateReportFieldValue>
}

export interface BuildTemplateIncidentPayloadInput {
  action: SafetyQrReportingTemplateActionDefinition
  form: TemplateReportFormState
  gpsCoordinates: ReportGpsCoordinates | null
  lookup: PublicLookupData | null
  preferredLanguage: "en" | "hi" | "ar" | null
  reportConfirmationAccepted: boolean
  template: SafetyQrReportingTemplateDefinition
  uploadedPhotos: PublicUploadReference[]
  uploadedVoiceNotes: PublicUploadReference[]
}

const NUMERIC_TAG_PATTERN = /^\d+$/
const INDIA_DIALING_CODE = "91"
const INDIA_PHONE_PREFIX = `+${INDIA_DIALING_CODE}`
const MIN_INTERNATIONAL_PHONE_DIGITS = 8
const MAX_INTERNATIONAL_PHONE_DIGITS = 15
const PHOTO_TYPES = new Set([
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
])
const PHOTO_FILE_NAME_PATTERN = /\.(jpe?g|png|webp|heic|heif)$/i

function normalizeIndianPhoneValue(value: string) {
  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return INDIA_PHONE_PREFIX
  }

  const digitsOnly = trimmedValue.replace(/\D/g, "")

  if (!digitsOnly) {
    return INDIA_PHONE_PREFIX
  }

  if (trimmedValue.startsWith("+")) {
    return `+${digitsOnly}`
  }

  if (trimmedValue.startsWith("00")) {
    return `+${digitsOnly.slice(2)}`
  }

  if (digitsOnly.startsWith(INDIA_DIALING_CODE)) {
    const localNumber = digitsOnly.slice(INDIA_DIALING_CODE.length)
    return localNumber ? `${INDIA_PHONE_PREFIX}${localNumber}` : INDIA_PHONE_PREFIX
  }

  if (digitsOnly.length > 10) {
    return `+${digitsOnly}`
  }

  return `${INDIA_PHONE_PREFIX}${digitsOnly}`
}

function getPhoneDigits(value: string) {
  const digitsOnly = value.replace(/\D/g, "")
  return digitsOnly
}

function isValidTemplatePhoneNumber(value: string) {
  const digitsOnly = getPhoneDigits(value)

  if (!digitsOnly) {
    return false
  }

  if (value.trim().startsWith(INDIA_PHONE_PREFIX)) {
    const localNumber = digitsOnly.startsWith(INDIA_DIALING_CODE)
      ? digitsOnly.slice(INDIA_DIALING_CODE.length)
      : digitsOnly
    return localNumber.length === 10
  }

  return (
    digitsOnly.length >= MIN_INTERNATIONAL_PHONE_DIGITS
    && digitsOnly.length <= MAX_INTERNATIONAL_PHONE_DIGITS
  )
}

function isTemplatePhoneField(field: SafetyQrReportingFieldDefinition | null | undefined) {
  return field?.kind === "tel"
}

function getTemplateFieldDefinition(
  template: SafetyQrReportingTemplateDefinition,
  fieldId: string | undefined
) {
  if (!fieldId) {
    return null
  }

  return template.fields.find((field) => field.id === fieldId) ?? null
}

export function normalizeTemplateFieldValue(
  field: SafetyQrReportingFieldDefinition,
  value: TemplateReportFieldValue
): TemplateReportFieldValue {
  if (!isTemplatePhoneField(field) || typeof value !== "string") {
    return value
  }

  return normalizeIndianPhoneValue(value)
}

export function createInitialReportForm(initialTag = ""): ReportFormState {
  return {
    behaviorType: "unsafe_driving",
    delay: "",
    description: "",
    deviceTag: initialTag,
    incidentType: "accident",
    injuryStatus: "unknown",
    locationDescription: "",
    peopleInvolved: "",
    reportKind: "incident",
    reporterEmail: "",
    reporterName: "",
    reporterPhone: "",
    severity: "",
    vehicleReference: "",
  }
}

export function validateIncidentForm(form: ReportFormState): string[] {
  const errors: string[] = []

  if (!form.deviceTag.trim()) {
    errors.push("Enter the SafetyQR tag before submitting the report.")
  }

  if (!form.severity) {
    errors.push("Choose a severity before submitting the report.")
  }

  if (!form.reporterPhone.trim()) {
    errors.push("Enter a phone number so responders can follow up if needed.")
  }

  if (form.reportKind === "incident") {
    if (!form.incidentType) {
      errors.push("Choose an incident type.")
    }

    if (!form.injuryStatus.trim()) {
      errors.push("Choose an injury status.")
    }

    if (!form.peopleInvolved.trim()) {
      errors.push("Choose how many people are involved.")
    }
  } else if (!form.behaviorType) {
    errors.push("Choose a behavior type.")
  }

  return errors
}

export function isAllowedIncidentPhoto(file: File): boolean {
  if (file.type && PHOTO_TYPES.has(file.type)) {
    return true
  }

  return PHOTO_FILE_NAME_PATTERN.test(file.name)
}

export function getResolvedReportTagType(lookup: PublicLookupData | null): TagType | null {
  return lookup?.tagType ?? null
}

function buildCombinedDescription(parts: string[]) {
  const normalizedParts = parts
    .map((part) => part.trim())
    .filter(Boolean)

  if (normalizedParts.length === 0) {
    return undefined
  }

  return normalizedParts.join("\n")
}

export function buildIncidentDescription(form: ReportFormState): string | undefined {
  return buildCombinedDescription([
    form.vehicleReference.trim()
      ? `Vehicle reference: ${form.vehicleReference.trim()}`
      : "",
    form.description.trim(),
  ])
}

function getTemplateFieldString(
  form: TemplateReportFormState,
  fieldId: string | undefined,
  template?: SafetyQrReportingTemplateDefinition
) {
  if (!fieldId) {
    return ""
  }

  const value = form.values[fieldId]
  const trimmedValue = typeof value === "string" ? value.trim() : ""

  if (!template) {
    return trimmedValue
  }

  const field = getTemplateFieldDefinition(template, fieldId)

  if (isTemplatePhoneField(field)) {
    return normalizeIndianPhoneValue(trimmedValue)
  }

  return trimmedValue
}

function getTemplateFieldBoolean(
  form: TemplateReportFormState,
  fieldId: string | undefined
) {
  if (!fieldId) {
    return false
  }

  return form.values[fieldId] === true
}

function buildTemplateDescription(
  template: SafetyQrReportingTemplateDefinition,
  form: TemplateReportFormState
) {
  const parts = template.normalization.descriptionFieldIds.map((fieldId) => {
    const value = getTemplateFieldString(form, fieldId, template)

    if (!value) {
      return ""
    }

    if (fieldId === "vehicleReference" || fieldId === "institutionContext") {
      const label = fieldId === "vehicleReference" ? "Vehicle reference" : "Institution context"
      return `${label}: ${value}`
    }

    return value
  })

  return buildCombinedDescription(parts)
}

function buildTemplatePayloadFields(
  template: SafetyQrReportingTemplateDefinition,
  form: TemplateReportFormState
) {
  return Object.fromEntries(
    template.fields.map((field) => {
      if (field.kind === "toggle") {
        return [field.id, getTemplateFieldBoolean(form, field.id)]
      }

      return [field.id, getTemplateFieldString(form, field.id, template)]
    })
  )
}

export function createInitialTemplateReportForm(
  initialTag: string,
  template: SafetyQrReportingTemplateDefinition,
  action?: SafetyQrReportingTemplateActionDefinition | null
): TemplateReportFormState {
  return {
    deviceTag: initialTag,
    values: Object.fromEntries(
      template.fields.map((field) => {
        const initialValue =
          action?.fieldDefaults?.[field.id] ??
          field.defaultValue ??
          (field.kind === "toggle" ? false : field.kind === "tel" ? INDIA_PHONE_PREFIX : "")

        return [field.id, normalizeTemplateFieldValue(field, initialValue)]
      })
    ),
  }
}

export function validateTemplateIncidentForm(
  template: SafetyQrReportingTemplateDefinition,
  action: SafetyQrReportingTemplateActionDefinition,
  form: TemplateReportFormState
): string[] {
  const errors: string[] = []

  if (!form.deviceTag.trim()) {
    errors.push("Enter the SafetyQR tag before submitting the report.")
  }

  const requiredFieldIds = new Set(action.requiredFieldIds)

  for (const field of template.fields) {
    if (!requiredFieldIds.has(field.id)) {
      continue
    }

    if (field.kind === "toggle") {
      if (typeof form.values[field.id] !== "boolean") {
        errors.push(`${field.label} is required.`)
      }
      continue
    }

    const normalizedValue = getTemplateFieldString(form, field.id, template)

    if (!normalizedValue) {
      errors.push(`${field.label} is required.`)
      continue
    }

    if (isTemplatePhoneField(field) && !isValidTemplatePhoneNumber(normalizedValue)) {
      errors.push(
        `${field.label} must include a valid phone number with 8 to 15 digits.`
      )
    }
  }

  return errors
}

export function buildPublicIncidentPayload({
  form,
  gpsCoordinates,
  lookup,
  preferredLanguage,
  uploadedPhotos,
  uploadedVoiceNotes,
}: BuildIncidentPayloadInput): PublicIncidentRequest {
  const deviceTag = form.deviceTag.trim()
  const payload: PublicIncidentRequest = {
    reportKind: form.reportKind,
    severity: form.severity,
    stickerId: deviceTag,
    tag: deviceTag,
  }
  const stickerType = getResolvedReportTagType(lookup)
  const description = buildIncidentDescription(form)

  if (NUMERIC_TAG_PATTERN.test(deviceTag)) {
    payload.cardNumber = deviceTag
  }

  if (stickerType) {
    payload.stickerType = stickerType
    payload.tagType = stickerType
  }

  if (form.reportKind === "incident") {
    payload.type = form.incidentType
    payload.injuryStatus = form.injuryStatus.trim()
    payload.peopleInvolved = form.peopleInvolved.trim()
  } else {
    payload.behaviorType = form.behaviorType
  }

  if (form.delay.trim()) {
    payload.delay = form.delay.trim()
  }

  if (form.reporterName.trim()) {
    payload.reporterName = form.reporterName.trim()
  }

  if (form.reporterPhone.trim()) {
    payload.reporterPhone = form.reporterPhone.trim()
  }

  if (form.reporterEmail.trim()) {
    payload.reporterEmail = form.reporterEmail.trim()
  }

  if (form.locationDescription.trim()) {
    payload.locationDescription = form.locationDescription.trim()
  }

  if (description) {
    payload.description = description
  }

  if (gpsCoordinates) {
    payload.gpsLatitude = gpsCoordinates.latitude.toFixed(8)
    payload.gpsLongitude = gpsCoordinates.longitude.toFixed(8)
    payload.gpsAccuracy = gpsCoordinates.accuracy.toFixed(2)
  }

  if (uploadedPhotos.length > 0) {
    payload.uploadedPhotos = uploadedPhotos
  }

  if (uploadedVoiceNotes.length > 0) {
    payload.uploadedVoiceNotes = uploadedVoiceNotes.map((voiceNote) => {
      const languageHint = voiceNote.languageHint || preferredLanguage || null
      return languageHint
        ? {
            ...voiceNote,
            languageHint,
          }
        : voiceNote
    })
  }

  if (preferredLanguage) {
    payload.preferredLanguage = preferredLanguage
  }

  return payload
}

export function buildTemplatePublicIncidentPayload({
  action,
  form,
  gpsCoordinates,
  lookup,
  preferredLanguage,
  reportConfirmationAccepted,
  template,
  uploadedPhotos,
  uploadedVoiceNotes,
}: BuildTemplateIncidentPayloadInput): PublicIncidentRequest {
  const deviceTag = form.deviceTag.trim()
  const payload: PublicIncidentRequest = {
    actionId: action.id,
    reportConfirmationAccepted,
    reportKind: template.normalization.reportKind,
    reportTemplateId: template.id,
    severity: getTemplateFieldString(
      form,
      template.normalization.severityFieldId,
      template
    ),
    stickerId: deviceTag,
    tag: deviceTag,
  }
  const stickerType = getResolvedReportTagType(lookup)
  const description = buildTemplateDescription(template, form)

  if (NUMERIC_TAG_PATTERN.test(deviceTag)) {
    payload.cardNumber = deviceTag
  }

  if (stickerType) {
    payload.stickerType = stickerType
    payload.tagType = stickerType
  }

  const normalizedType = getTemplateFieldString(
    form,
    template.normalization.typeFieldId,
    template
  )
  const normalizedInjuryStatus = getTemplateFieldString(
    form,
    template.normalization.injuryStatusFieldId,
    template
  )
  const normalizedPeopleInvolved = getTemplateFieldString(
    form,
    template.normalization.peopleInvolvedFieldId,
    template
  )
  const normalizedDelay = getTemplateFieldString(
    form,
    template.normalization.delayFieldId,
    template
  )
  const normalizedLocation = getTemplateFieldString(
    form,
    template.normalization.locationDescriptionFieldId,
    template
  )
  const normalizedReporterName = getTemplateFieldString(
    form,
    template.normalization.reporterNameFieldId,
    template
  )
  const normalizedReporterPhone = getTemplateFieldString(
    form,
    template.normalization.reporterPhoneFieldId,
    template
  )
  const normalizedReporterEmail = getTemplateFieldString(
    form,
    template.normalization.reporterEmailFieldId,
    template
  )

  if (normalizedType) {
    payload.type = normalizedType
  }

  if (normalizedInjuryStatus) {
    payload.injuryStatus = normalizedInjuryStatus
  }

  if (normalizedPeopleInvolved) {
    payload.peopleInvolved = normalizedPeopleInvolved
  }

  if (normalizedDelay) {
    payload.delay = normalizedDelay
  }

  if (normalizedLocation) {
    payload.locationDescription = normalizedLocation
  }

  if (normalizedReporterName) {
    payload.reporterName = normalizedReporterName
  }

  if (normalizedReporterPhone) {
    payload.reporterPhone = normalizedReporterPhone
  }

  if (normalizedReporterEmail) {
    payload.reporterEmail = normalizedReporterEmail
  }

  if (description) {
    payload.description = description
  }

  if (gpsCoordinates) {
    payload.gpsLatitude = gpsCoordinates.latitude.toFixed(8)
    payload.gpsLongitude = gpsCoordinates.longitude.toFixed(8)
    payload.gpsAccuracy = gpsCoordinates.accuracy.toFixed(2)
  }

  if (uploadedPhotos.length > 0) {
    payload.uploadedPhotos = uploadedPhotos
  }

  if (uploadedVoiceNotes.length > 0) {
    payload.uploadedVoiceNotes = uploadedVoiceNotes.map((voiceNote) => {
      const languageHint = voiceNote.languageHint || preferredLanguage || null
      return languageHint
        ? {
            ...voiceNote,
            languageHint,
          }
        : voiceNote
    })
  }

  if (preferredLanguage) {
    payload.preferredLanguage = preferredLanguage
  }

  payload.reportPayload = {
    actionId: action.id,
    fields: buildTemplatePayloadFields(template, form),
    templateId: template.id,
  }

  return payload
}
